import { useEffect, useState } from 'react';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { C } from '../../theme/palette';
import { RelierApplication } from '../plus/RelierApplication.jsx';

/** Longueur du code reçu par e-mail. */
const LONGUEUR_CODE = 6;

const OPTIONS = [
  {
    id: 'APPLICATION',
    titre: '🔑 Application Authenticator',
    detail: 'Google ou Microsoft Authenticator',
    sur: true,
  },
  { id: 'EMAIL', titre: '✉️ Code par e-mail', detail: 'À ton adresse de connexion' },
  { id: 'SMS', titre: '📱 Code par SMS', detail: 'Sur ton portable' },
];

/**
 * Premier passage d'un compte neuf — la direction qui vient de s'inscrire,
 * la personne qui vient d'accepter son invitation : avant d'entrer, elle
 * choisit d'où viendra le code de chaque connexion.
 *
 * <p>Le mot de passe est redemandé, comme dans Sécurité : une session laissée
 * ouverte ne suffit pas à changer ce qui protège la suivante. Si l'envoi d'un
 * code échoue, « Plus tard » laisse entrer : le choix sera reproposé à la
 * prochaine connexion, personne ne reste dehors.
 *
 * <p>Ouvert depuis Réglages (`depuisReglages`), il change la 2ᵉ sécurité quand
 * on veut : « ← Retour » le referme sans rien changer.
 *
 * @param {object} props propriétés
 * @param {Function} props.onTermine appelée une fois la sécurité choisie
 * @param {boolean} [props.sansReport] compte neuf : le choix ne se remet pas
 * @param {boolean} [props.depuisReglages] ouvert depuis Réglages → Mon compte
 * @returns {JSX.Element} l'écran
 */
export function ChoixSecuriteEcran({ onTermine, sansReport = true, depuisReglages = false }) {
  const { session, deconnexion } = useSession();
  // La direction protège sa société ; un employé invité (ouvrier, technicien…)
  // ne fait que choisir d'où vient son code : le plus simple pour lui, le code
  // par e-mail, est proposé d'office — l'application Authenticator, choisie
  // d'office, arrêtait l'ouvrier qui touchait « Continuer ».
  const patron = !session?.role || session.role === 'patron';
  const [smsDisponible, setSmsDisponible] = useState(false);
  const [choix, setChoix] = useState(patron ? 'APPLICATION' : 'EMAIL');
  const [etape, setEtape] = useState('choix');
  const [motDePasse, setMotDePasse] = useState('');
  const [telephone, setTelephone] = useState('');
  const [defi, setDefi] = useState('');
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // La sécurité que le compte a déjà : le code par e-mail (ou SMS) se garde
  // d'un toucher ; l'écran revient à chaque connexion pour proposer mieux.
  const [actuelle, setActuelle] = useState(null);

  useEffect(() => {
    let annule = false;
    authApi
      .etatDoubleAuth()
      .then((etat) => {
        if (annule) return;
        setSmsDisponible(Boolean(etat?.smsDisponible));
        if (etat?.telephone) setTelephone(etat.telephone);
        // La 2ᵉ sécurité choisie, telle que le serveur l'applique à la connexion.
        if (etat?.methode) setActuelle(etat.methode);
        else if (etat?.application) setActuelle('APPLICATION');
        else if (etat?.active || etat?.obligatoire) setActuelle('EMAIL');
        if (etat?.methode) setChoix(etat.methode);
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, []);

  const tente = async (action) => {
    setErreur('');
    setEnvoi(true);
    try {
      await action();
    } catch (e) {
      setErreur(e?.message || 'La demande n’a pas abouti.');
    } finally {
      setEnvoi(false);
    }
  };

  // Le premier code part par la voie choisie : il prouve l'adresse, ou que
  // le numéro de portable est juste.
  const envoieLePremierCode = () =>
    tente(async () => {
      if (choix === 'SMS') await authApi.changeTelephoneCode(motDePasse, telephone.trim());
      const reponse = await authApi.activeDoubleAuth(
        motDePasse,
        choix === 'SMS' ? 'SMS' : undefined,
      );
      setDefi(reponse.defi);
      setMotDePasse('');
      setCode('');
    });

  const confirme = () =>
    tente(async () => {
      await authApi.confirmeDoubleAuth(defi, code, etape);
      onTermine?.();
    });

  const options = OPTIONS.filter((o) => o.id !== 'SMS' || smsDisponible);

  // Garder la sécurité actuelle : rien à refaire, l'écran ne revient qu'à la
  // prochaine connexion.
  const garde = () => {
    if (!depuisReglages) authApi.reporteChoixSecurite?.().catch(() => {});
    onTermine?.();
  };
  const continuer = () => (choix === actuelle ? garde() : setEtape(choix));

  const option = (o) => {
    const actif = choix === o.id;
    return (
      <button
        key={o.id}
        type="button"
        role="radio"
        aria-checked={actif}
        onClick={() => setChoix(o.id)}
        style={{
          display: 'flex',
          gap: 11,
          alignItems: 'flex-start',
          width: '100%',
          textAlign: 'left',
          border: '1.5px solid ' + (actif ? C.teal : C.border),
          background: actif ? C.tealL : C.white,
          borderRadius: 12,
          padding: 12,
          marginBottom: 10,
          cursor: 'pointer',
          font: 'inherit',
          color: C.ink,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: actif ? '6px solid ' + C.teal : '2px solid #CBD5E1',
            boxSizing: 'border-box',
            flexShrink: 0,
            marginTop: 2,
          }}
        />
        <span>
          <b style={{ fontSize: 13.5 }}>{o.titre}</b>
          {o.id === actuelle && (
            <span
              style={{
                display: 'inline-block',
                background: C.navyXL,
                color: C.navy,
                fontSize: 9.5,
                fontWeight: 800,
                borderRadius: 6,
                padding: '2px 7px',
                marginLeft: 6,
                verticalAlign: 2,
              }}
            >
              ACTUELLE
            </span>
          )}
          {o.sur && (
            <span
              style={{
                display: 'inline-block',
                background: C.greenL,
                color: C.green,
                fontSize: 9.5,
                fontWeight: 800,
                borderRadius: 6,
                padding: '2px 7px',
                marginLeft: 6,
                verticalAlign: 2,
              }}
            >
              LE PLUS SÛR
            </span>
          )}
          <span style={{ display: 'block', fontSize: 11.5, color: C.grey, marginTop: 3 }}>
            {o.detail}
          </span>
        </span>
      </button>
    );
  };

  const lien = {
    background: 'none',
    border: 'none',
    color: C.teal,
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
    marginTop: 12,
    display: 'block',
    width: '100%',
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <div
        style={{
          background: C.navy,
          color: '#fff',
          padding: 'calc(var(--st-haut) + 14px) 16px 14px',
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 900 }}>SwissTech</div>
        <div style={{ fontSize: 10, letterSpacing: 2, color: '#94A3B8' }}>PROTECTION DU COMPTE</div>
      </div>
      <div style={{ padding: 14, maxWidth: 480, margin: '0 auto' }}>
        <div
          style={{
            background: C.white,
            borderRadius: 14,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,.06)',
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid ' + C.border,
              fontWeight: 800,
              fontSize: 15,
              background: C.navyXL,
            }}
          >
            🔒 Protège ton compte
          </div>
          <div style={{ padding: 14, fontSize: 13, lineHeight: 1.45, color: C.ink }}>
            {etape === 'choix' && (
              <>
                {depuisReglages ? (
                  <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10 }}>
                    Choisis d’où vient le code de chaque connexion. Tu peux le changer quand tu
                    veux.
                  </div>
                ) : actuelle === 'EMAIL' ? (
                  <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10 }}>
                    Ta 2ᵉ sécurité actuelle : le code par e-mail. Garde-la, ou choisis l’application
                    Authenticator, plus sûre.
                  </div>
                ) : actuelle === 'SMS' ? (
                  <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10 }}>
                    Ta 2ᵉ sécurité actuelle : le code par SMS. Garde-la, ou choisis l’application
                    Authenticator, plus sûre.
                  </div>
                ) : !patron && sansReport ? (
                  <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10 }}>
                    Dernière étape : choisis comment SwissTech vérifie que c’est bien toi. Après ton
                    mot de passe, il te demandera un code à 6 chiffres. Le plus simple : le code par
                    e-mail, à ton adresse de connexion. Tu pourras le changer plus tard dans Plus →
                    Sécurité.
                  </div>
                ) : !patron ? (
                  <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10 }}>
                    Pour mieux protéger ton compte, choisis comment SwissTech vérifie que c’est bien
                    toi. Après ton mot de passe, il te demandera un code à 6 chiffres. Tu pourras le
                    changer plus tard dans Plus → Sécurité.
                  </div>
                ) : sansReport ? (
                  <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10 }}>
                    Dernière étape. Ta société, tes clients et tes factures seront ici : choisis ta
                    2ᵉ sécurité. Après ton mot de passe, SwissTech te demandera un code à 6
                    chiffres. Tu pourras la changer plus tard dans Plus → Sécurité.
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10 }}>
                    Pour mieux protéger ta société, tes clients et tes factures, choisis ta 2ᵉ
                    sécurité. Après ton mot de passe, SwissTech te demandera un code à 6 chiffres.
                    Tu pourras la changer plus tard dans Plus → Sécurité.
                  </div>
                )}
                <div role="radiogroup" aria-label="Ma 2ᵉ sécurité">
                  {options.map(option)}
                </div>
                {!smsDisponible && (
                  <div
                    style={{ fontSize: 11, color: C.grey, margin: '-2px 0 10px', lineHeight: 1.45 }}
                  >
                    📱 Le code par SMS sera proposé dès qu’un fournisseur de SMS sera raccordé à
                    SwissTech.
                  </div>
                )}
                <Btn label="Continuer" color={C.teal} full onClick={continuer} />
                {depuisReglages && (
                  <button type="button" style={lien} onClick={() => onTermine?.()}>
                    ← Retour sans rien changer
                  </button>
                )}
              </>
            )}

            {etape === 'APPLICATION' && <RelierApplication onRelie={() => onTermine?.()} />}

            {(etape === 'EMAIL' || etape === 'SMS') && !defi && (
              <>
                <div style={{ fontSize: 12.5, color: C.grey, marginBottom: 10 }}>
                  Un premier code part à <b>{session?.email}</b> : il prouve que cette adresse
                  reçoit bien le courrier.
                </div>
                {etape === 'SMS' && (
                  <Inp
                    label="Numéro de portable"
                    placeholder="+41 79 123 45 67"
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                  />
                )}
                <Inp
                  label="Mot de passe actuel"
                  type="password"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                />
                <Btn
                  label={envoi ? 'Envoi du code…' : 'Recevoir le code'}
                  color={C.teal}
                  full
                  disabled={envoi || !motDePasse || (etape === 'SMS' && !telephone.trim())}
                  onClick={envoieLePremierCode}
                />
              </>
            )}

            {(etape === 'EMAIL' || etape === 'SMS') && defi && (
              <>
                <Inp
                  label={etape === 'SMS' ? 'Saisis le code reçu par SMS' : 'Code reçu par e-mail'}
                  placeholder="6 chiffres"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, LONGUEUR_CODE))
                  }
                />
                <Btn
                  label={envoi ? 'Vérification…' : '✓ Activer'}
                  color={C.teal}
                  full
                  disabled={envoi || code.length !== LONGUEUR_CODE}
                  onClick={confirme}
                />
              </>
            )}

            {erreur && (
              <div
                role="alert"
                style={{ fontSize: 10.5, color: C.red, fontWeight: 700, marginTop: 8 }}
              >
                {erreur}
              </div>
            )}

            {etape !== 'choix' && (
              <button
                type="button"
                style={lien}
                onClick={() => {
                  setEtape('choix');
                  setDefi('');
                  setCode('');
                  setErreur('');
                }}
              >
                ← Changer de choix
              </button>
            )}
            {/* Un compte qui existait déjà peut remettre le choix : l'écran
                revient à chaque connexion tant qu'il n'est pas fait. Le compte
                neuf ne le peut que si l'envoi d'un code échoue. */}
            {!depuisReglages && (erreur || (!sansReport && !actuelle)) && (
              <button
                type="button"
                style={{ ...lien, color: C.grey }}
                onClick={() => {
                  // Retenu pour la connexion en cours ; s'il échoue, on entre quand même.
                  if (!sansReport) authApi.reporteChoixSecurite?.().catch(() => {});
                  onTermine?.();
                }}
              >
                Plus tard
              </button>
            )}
          </div>
        </div>
        {!depuisReglages && (
          <>
            {patron && (
              <div
                style={{
                  fontSize: 11,
                  color: C.grey,
                  textAlign: 'center',
                  marginTop: 14,
                  lineHeight: 1.5,
                }}
              >
                {/* L'ouvrier entre par son e-mail et son mot de passe : la 2ᵉ
                    sécurité ne lui est pas demandée (il l'allume s'il veut). */}
                Les employés invités (secrétaire, technicien, comptable) font le même choix quand
                ils créent leur accès.
              </div>
            )}
            <button
              type="button"
              style={{ ...lien, color: C.muted, fontWeight: 600, fontSize: 11 }}
              onClick={() => deconnexion?.()}
            >
              Se déconnecter
            </button>
          </>
        )}
      </div>
    </div>
  );
}
